
import { Injectable } from "@angular/core";
import { ISafeFileSystem } from "./safeFileSystem.service";
import { env } from "../../../environments/environment";
import { Client } from "./com/comEnvironmentClient";
import { EnvDir } from "./com/comEnvironmentFileSystem";
import { Serializer } from "../shared/serializer";
import Utils from "../shared/utils";
import * as moment from "moment";
import { Observable, Subject } from "rxjs";

/** Logging */

export interface ILog {
  debug(caller: string, msg: string, ...details: any[]): void;
  info(caller: string, msg: string, ...details: any[]): void;
  error(caller: string, msg: string, ...details: any[]): void;
  readDebug(): Promise<string>;
  readInfo(): Promise<string>;
  readError(): Promise<string>;
  clear(): void;
  /** wird ausgelöst, wenn eine debug log ausgelöst wird */
  debugLogReceived: Observable<string>;
  /** wird ausgelöst, wenn eine info log ausgelöst wird */
  infoLogReceived: Observable<string>;
  /** wird ausgelöst, wenn eine error log ausgelöst wird */
  errorLogReceived: Observable<string>;
}

@Injectable()
export class CoreLogService implements ILog {
  // loglevels, error = 0, info (+error) = 1, debug (+error +info) = 2
  private logLevelError: number = 0;
  private logLevelInfo: number = 1;
  private logLevelDebug: number = 2;

  /** Property to acces to the Filesystem */
  private _safeFileSystem: ISafeFileSystem = null;
  private _client: Client = null;

  private _debugLogFile: FileEntry = null;
  private _infoLogFile: FileEntry = null;
  private _errorLogFile: FileEntry = null;

  private _debugLogReceived: Subject<string> = new Subject();
  /** wird ausgelöst, wenn eine debug log ausgelöst wird */
  public get debugLogReceived(): Observable<string> {
    return this._debugLogReceived.asObservable();
  }
  private _infoLogReceived: Subject<string> = new Subject();
  /** wird ausgelöst, wenn eine info log ausgelöst wird */
  public get infoLogReceived(): Observable<string> {
    return this._infoLogReceived.asObservable();
  }
  private _errorLogReceived: Subject<string> = new Subject();
  /** wird ausgelöst, wenn eine error log ausgelöst wird */
  public get errorLogReceived(): Observable<string> {
    return this._errorLogReceived.asObservable();
  }

  /** UAPP CoreLogService for Logging */
  public constructor() {
  }

  public setSafeFileSystem(file_access: ISafeFileSystem, client: Client): void {
    const me = this;
    if (me._safeFileSystem !== null) {
      throw new Error("SafeFileSystem is already set.");
    }

    me._safeFileSystem = file_access;
    me._client = client;
    // momentan verfügen nur die mobilen clienten über einen Speicher
    if (me._client.isMobile) {
      me._safeFileSystem.createFile(EnvDir.Cache, "log", "debug.log")
        .then((file) => { me._debugLogFile = file; });
      me._safeFileSystem.createFile(EnvDir.Cache, "log", "info.log")
        .then((file) => { me._infoLogFile = file; });
      me._safeFileSystem.createFile(EnvDir.Cache, "log", "error.log")
        .then((file) => { me._errorLogFile = file; });
    }
  }

  public debug(caller: string, msg: string, ...details: any[]): void {
    if (env.log.level >= this.logLevelDebug) {
      this.writeToFile("debug", this._debugLogFile, caller, msg, details);
    }
  }

  public info(caller: string, msg: string, ...details: any[]): void {
    if (env.log.level >= this.logLevelInfo) {
      this.writeToFile("info", this._infoLogFile, caller, msg, details);
    }
  }

  public error(caller: string, msg: string, ...details: any[]): void {
    if (env.log.level >= this.logLevelError) {
      this.writeToFile("error", this._errorLogFile, caller, msg, details);
    }
  }

  private createLogText(caller: string, msg: string, details: any[]): string {
    const timestamp = moment().format("YY.MM.DD HH:mm:ss.SSS");
    return `${timestamp}>[${caller}]: ${msg}${this.detailsToString(details)}\r\n`;
  }

  private writeToFile(typ: string, file: FileEntry, caller: string, msg: string, details: any[]): void {
    const useFileLog = file !== null;
    const useConsoleLog = !useFileLog || env.debug.mobileUseConsoleLogToo;

    // hier ist als einziges console.log erlaubt, da es nur ein fall back ist, wenn auf das Filesystem nicht zugegriffen werden kann.
    if (useFileLog) {
      // momentan verfügen nur die mobilen clienten über einen Speicher
      if (this._client.isMobile) {
        this._safeFileSystem.writeStringToFileByEntry(file, this.createLogText(caller, msg, details), true)
          .catch((error) => {
            // tslint:disable-next-line: no-console
            console.error("Log.writeToFile");
            // tslint:disable-next-line: no-console
            console.error(error);
          });
      }
    }
    if (useConsoleLog) {
      if (typ === "error") {
        // tslint:disable-next-line: no-console
        console.error(this.createLogText(caller, msg, details));
      } else if (typ === "debug") {
        // tslint:disable-next-line: no-console
        console.debug(this.createLogText(caller, msg, details));
      } else /* if (typ === "info") */ {
        // tslint:disable-next-line: no-console
        console.log(this.createLogText(caller, msg, details));
      }
    }
    if (typ === "error" && this._errorLogReceived.observers.length > 0) {
      // tslint:disable-next-line: no-console
      this._errorLogReceived.next(this.createLogText(caller, msg, details));
    } else if (typ === "debug" && this._debugLogReceived.observers.length > 0) {
      // tslint:disable-next-line: no-console
      this._debugLogReceived.next(this.createLogText(caller, msg, details));
    } else if (typ === "info" && this._infoLogReceived.observers.length > 0) {
      // tslint:disable-next-line: no-console
      this._infoLogReceived.next(this.createLogText(caller, msg, details));
    }
  }

  /** Wandelt die 'details' in ein JSON string */
  private detailsToString(details: any[]): string {
    if (Utils.IsNull(details) || details.length === 0) {
      return "";
    } else {
      try {
        return " Details: " + Serializer.serialize(details);
      } catch (error) {
        if (details[0] instanceof Error){
          return " Details: " + details[0].stack;
        } else {
          return " Details serialize error: " + error;
        }
      }
    }
  }

  public readDebug(): Promise<string> {
    return this.readFile(this._debugLogFile);
  }

  public readInfo(): Promise<string> {
    return this.readFile(this._infoLogFile);
  }

  public readError(): Promise<string> {
    return this.readFile(this._errorLogFile);
  }

  /** Read file content as string
   * @param file file for reading
   * @returns content as string or if no file there a emptystring
   */
  private readFile(file: FileEntry): Promise<string> {
    if (file !== null) {
      // momentan verfügen nur die mobilen clienten über einen Speicher
      if (this._client.isMobile) {
        return this._safeFileSystem.readStringFromFileByEntry(file);
      } else {
        return new Promise<string>((resolve) => { resolve(""); });
      }
    } else {
      return new Promise<string>((resolve) => { resolve(""); });
    }
  }

  public clear(): void {
    this.clearFile(this._debugLogFile);
    this.clearFile(this._infoLogFile);
    this.clearFile(this._errorLogFile);
  }

  private clearFile(file: FileEntry): void {
    if (file !== null) {
      // momentan verfügen nur die mobilen clienten über einen Speicher
      if (this._client.isMobile) {
        this._safeFileSystem.writeStringToFileByEntry(file, "", false);
      }
    }
  }

}
